import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import type { ChatOverview } from '@/types/teacher-chat'
import { teacherChatApi } from '@/services/api/teacherChat'
import { useToast } from '@/components/ui/ToastProvider'
import { AvatarStack } from './AvatarStack'
import { ParticipantBadge } from './ParticipantBadge'
import { TeacherSearchDropdown } from '@/components/teacher-request/TeacherSearchDropdown'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'

function StatusBadge({ status }: { status: string }) {
  const directorOn = useDirectorEnabled()
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Anfrage offen', cls: 'bg-yellow-100 text-yellow-800' },
    partially_accepted: { label: 'Teils angenommen', cls: 'bg-blue-100 text-blue-800' },
    accepted: { label: 'Angenommen', cls: 'bg-green-100 text-green-800' },
    booked: { label: 'Gebucht', cls: 'bg-green-100 text-green-800' },
    declined: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-800' },
    failed: { label: 'Gescheitert', cls: 'bg-red-100 text-red-800' },
    no_substitute_found: { label: 'Keine Vertretung gefunden', cls: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Storniert', cls: 'bg-gray-100 text-gray-800' },
    expired: { label: 'Abgelaufen', cls: 'bg-gray-100 text-gray-800' },
    // R5: Director-Gate-Status im Chat-Badge sichtbar
    ...(directorOn ? {
      director_pending: { label: 'Freigabe ausstehend', cls: 'bg-indigo-100 text-indigo-800' },
      director_returned: { label: 'Zurückgestellt', cls: 'bg-orange-100 text-orange-800' },
    } : {}),
  }
  const s = (!directorOn && (status === 'director_pending' || status === 'director_returned'))
    ? { label: 'Anfrage offen', cls: 'bg-yellow-100 text-yellow-800' }
    : (map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' })
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full font-medium shrink-0 ${s.cls}`}>
      {s.label}
    </span>
  )
}

interface ChatHeaderProps {
  chat: ChatOverview
  onClose?: () => void
}

export function ChatHeader({ chat, onClose }: ChatHeaderProps) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [showSearch, setShowSearch] = useState(false)

  const handleAdd = async (teacher: { id: string }) => {
    try {
      await teacherChatApi.addParticipant(chat.request_id, teacher.id)
      await queryClient.invalidateQueries({ queryKey: ['teacher-chats'] })
      showToast({ message: 'Teilnehmer hinzugefügt', type: 'success', duration: 3000 })
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Teilnehmer konnte nicht hinzugefügt werden'
      showToast({ message: detail, type: 'error', duration: 5000 })
    }
  }

  const handleRemove = async (teacherId: string) => {
    if (!confirm('Teilnehmer wirklich entfernen?')) return
    try {
      await teacherChatApi.removeParticipant(chat.request_id, teacherId)
      await queryClient.invalidateQueries({ queryKey: ['teacher-chats'] })
      showToast({ message: 'Teilnehmer entfernt', type: 'success', duration: 3000 })
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Teilnehmer konnte nicht entfernt werden'
      showToast({ message: detail, type: 'error', duration: 5000 })
    }
  }

  const subjectLabel = useMemo(() => {
    const subj = chat.subjects_summary || chat.subject_name
    const klass = chat.classes_summary || chat.klass_name
    const parts: string[] = []
    if (subj) parts.push(subj)
    if (klass) parts.push(klass)
    return parts.join(' · ') || 'Fach'
  }, [chat.subjects_summary, chat.classes_summary, chat.subject_name, chat.klass_name])

  return (
    <div className="relative z-20 bg-white border-b border-gray-200 px-4 md:px-6 py-3 shrink-0 min-w-0">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <AvatarStack participants={chat.participants} size="md" />
          <div className="min-w-0">
            <div className="font-semibold text-sm text-gray-900 truncate">
              Vertretung {subjectLabel}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span>{chat.participants.length} Teilnehmer</span>
              <span className="text-gray-300">·</span>
              <span>{chat.lesson_count} Lektion{chat.lesson_count !== 1 ? 'en' : ''}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={chat.request_status} />
          <Link
            to={`/teacher/incoming-requests?highlight=${chat.request_id}`}
            className="text-xs text-blue-600 hover:underline font-medium flex items-center gap-1"
            aria-label="Zur Anfrage wechseln"
          >
            <ExternalLink className="w-3 h-3" />
            <span className="hidden sm:inline">Zur Anfrage</span>
          </Link>
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden p-1 rounded hover:bg-gray-100"
              aria-label="Chat schliessen"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
          Teilnehmer:
        </span>
        {chat.participants.map(p => (
          <span key={p.teacher_id} className="inline-flex items-center">
            <ParticipantBadge participant={p} />
            {!p.is_requesting_teacher && !p.is_current_user && (
              <button
                onClick={() => handleRemove(p.teacher_id)}
                className="text-gray-300 hover:text-red-500 ml-0.5 text-xs"
                aria-label={`${p.teacher_name} entfernen`}
              >
                ×
              </button>
            )}
          </span>
        ))}

        <div className="relative">
          <button
            onClick={() => setShowSearch(v => !v)}
            className="text-[10px] text-blue-600 hover:underline font-medium ml-1"
            aria-label="Teilnehmer einladen"
          >
            + Einladen
          </button>
          {showSearch && (
            <TeacherSearchDropdown
              excludeTeacherIds={chat.participants.map(p => p.teacher_id)}
              onSelect={handleAdd}
              onClose={() => setShowSearch(false)}
            />
          )}
        </div>
      </div>
    </div>
  )
}
